import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';

import type { PopoverFilterEntry } from '~/components/Popover';
import { useQueryParams } from '~/hooks/useQueryParams';
import { clientWrapper } from '~/repositories/client';
import { useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

export interface CursorPaginationResponse<T> {
  entries: T[];
  hasMore: boolean;
}

export interface UseCursorPaginationOptions<T> {
  tableId: string;
  fetch: (params: any) => Promise<CursorPaginationResponse<T>>;
  fetchSearch?: (params: any, search: string) => Promise<CursorPaginationResponse<T>>;
  fetchId?: string;
  filter?: PopoverFilterEntry[];
  syncQueryParams?: boolean;
  onError?: (error: any) => void;
}

interface FetchOptions {
  startingAfter?: string;
  endingBefore?: string;
  limit?: number;
  page?: number;
}

const DATE_OPERATORS = ['gt', 'gte', 'lt', 'lte', 'eq'];

const getFilterKeys = (filters: PopoverFilterEntry[]) =>
  filters.flatMap((entry) =>
    entry.type === 'date' ? DATE_OPERATORS.map((operator) => `${entry.id}.${operator}`) : [entry.id]
  );

const restoreFilters = (filters: PopoverFilterEntry[], params: Record<string, any>) =>
  filters.map((entry) => {
    if (entry.type === 'date') {
      const operator = DATE_OPERATORS.find((key) => params[`${entry.id}.${key}`] !== undefined);
      return operator
        ? { ...entry, show: true, typeActive: operator, active: String(params[`${entry.id}.${operator}`]) }
        : entry;
    }
    return params[entry.id] !== undefined ? { ...entry, show: true, active: String(params[entry.id]) } : entry;
  });

export const useCursorPagination = <T extends Record<string, any>>({
  tableId,
  fetch,
  fetchSearch,
  fetchId = 'id',
  filter,
  syncQueryParams = false,
  onError,
}: UseCursorPaginationOptions<T>) => {
  const currentUser = useSessionStore((state) => state.currentUser);
  const { pageSize, update: updateTableConfig } = useTableConfig(tableId);
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);
  const { queryParams: urlQueryParams, setQueryParams: setUrlQueryParams } = useQueryParams<Record<string, any>>();
  const initialUrlParams = useRef(syncQueryParams ? (urlQueryParams ?? {}) : {});
  const initialCursor = useRef<FetchOptions | undefined>(
    initialUrlParams.current.startingAfter || initialUrlParams.current.endingBefore
      ? {
          startingAfter: initialUrlParams.current.startingAfter
            ? String(initialUrlParams.current.startingAfter)
            : undefined,
          endingBefore: initialUrlParams.current.endingBefore
            ? String(initialUrlParams.current.endingBefore)
            : undefined,
          page: Number(initialUrlParams.current.page) || 1,
        }
      : undefined
  );
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [list, setList] = useState<T[]>([]);
  const [filters, setFilters] = useState<PopoverFilterEntry[]>(() =>
    restoreFilters(filter ?? [], initialUrlParams.current)
  );
  const [searchTerm, setSearchTerm] = useState(() =>
    fetchSearch && initialUrlParams.current.search ? String(initialUrlParams.current.search) : ''
  );

  const filterObject = useMemo(
    () =>
      Object.assign(
        {},
        ...filters
          .map((entry) => {
            if (!entry.show) {
              return undefined;
            }
            if (entry.type === 'date' && entry.active) {
              return { [`${entry.id}.${entry.typeActive ?? 'gt'}`]: new Date(entry.active).toISOString() };
            }
            return { [entry.id]: entry.active };
          })
          .filter(Boolean)
      ),
    [filters]
  );

  const isFiltered = searchTerm.length > 0 || Object.keys(filterObject).length !== 0;

  const writeUrl = (page: number, startingAfter?: string, endingBefore?: string) => {
    if (!syncQueryParams) {
      return;
    }

    const ownedKeys = ['page', 'startingAfter', 'endingBefore', 'search', ...getFilterKeys(filters)];

    setUrlQueryParams((previous) => {
      const next: Record<string, any> = { ...(previous ?? {}) };
      ownedKeys.forEach((key) => delete next[key]);

      if (page > 1) {
        next.page = page;
        next.startingAfter = startingAfter;
        next.endingBefore = endingBefore;
      }
      if (searchTerm.length > 0) {
        next.search = searchTerm;
      }

      return { ...next, ...filterObject };
    });
  };

  const requestId = useRef(0);

  const fetchData = (options: FetchOptions = {}) => {
    if (!currentUser) {
      return;
    }

    const id = ++requestId.current;
    const isStale = () => id !== requestId.current;

    const cursor = !options.startingAfter && !options.endingBefore ? initialCursor.current : undefined;
    initialCursor.current = undefined;

    const startingAfter = cursor?.startingAfter ?? options.startingAfter;
    const endingBefore = cursor?.endingBefore ?? options.endingBefore;
    const page = cursor?.page ?? options.page ?? 1;

    setLoading(true);

    const handleResponse = (response: CursorPaginationResponse<T>) => {
      if (isStale()) {
        return;
      }

      if (cursor && response.entries.length === 0) {
        fetchRef.current();
        return;
      }

      setCurrentPage(page);
      setHasMore(!startingAfter && endingBefore ? true : response.hasMore);
      setList(response.entries);
      setLoading(false);
      setHasLoaded(true);
      writeUrl(page, startingAfter, endingBefore);
    };

    const handleError = (error: any) => {
      if (isStale()) {
        return;
      }

      if (cursor) {
        fetchRef.current();
        return;
      }

      setLoading(false);
      setHasLoaded(true);

      if (onError) {
        onError(error);
        return;
      }

      if (error?.type === 'request_validation_error') {
        addDangerNotification({ content: error.message });
        console.warn('useCursorPagination.RequestValidationError', error);
        return;
      }

      console.error('useCursorPagination', error);
    };

    setTimeout(() => {
      const params = { startingAfter, endingBefore, limit: options.limit ?? pageSize, ...filterObject };

      if (searchTerm.length > 0 && fetchSearch) {
        setSearchLoading(true);
        fetchSearch(params, searchTerm)
          .then(handleResponse)
          .catch(handleError)
          .finally(() => {
            if (!isStale()) {
              setSearchLoading(false);
            }
          });
        return;
      }

      setSearchLoading(false);
      clientWrapper(handleResponse, handleError, fetch(params));
    }, 100);
  };

  const fetchRef = useRef(fetchData);
  fetchRef.current = fetchData;

  const refetch = useCallback(() => fetchRef.current(), []);

  const isMounted = useRef(false);

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => [state.isSandbox, state.isLoading, state.currentUser?.defaultPlatformId],
        () => fetchRef.current(),
        { fireImmediately: true, equalityFn: shallow }
      ),
    [pageSize]
  );

  useEffect(() => {
    if (!isMounted.current) {
      return;
    }
    fetchRef.current();
  }, [searchTerm]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    fetchRef.current();
  }, [filterObject]);

  const onPrevious = () => {
    fetchData({ endingBefore: list[0]?.[fetchId], limit: pageSize, page: currentPage - 1 });
  };

  const onNext = () => {
    fetchData({ startingAfter: list[list.length - 1]?.[fetchId], limit: pageSize, page: currentPage + 1 });
  };

  const onPageSizeChange = (value: number) => {
    updateTableConfig({ limit: value }).catch(() => {
      addDangerNotification({ content: 'Failed to save page size preference' });
    });
    fetchData({ limit: value });
  };

  return {
    list,
    isLoading: loading,
    hasLoaded,
    isFiltered,
    filters,
    setFilters,
    searchTerm,
    searchLoading,
    onSearchSubmit: fetchSearch ? setSearchTerm : undefined,
    refetch,
    pagination: {
      pageSize,
      onPageSizeChange,
      current: currentPage,
      hasPrevious: currentPage > 1,
      hasNext: hasMore,
      onPrevious,
      onNext,
    },
  };
};
